// src/pages/CourseDetailPage.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams, Link } from "react-router-dom";
import { db } from "./firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";

// ===== Styled Components =====
const PageContainer = styled.div`
  font-family: "Roboto", sans-serif;
  background: #0d0d0d;
  color: #fff;
  min-height: 100vh;
  padding: 60px 20px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: auto;
  gap: 40px;
`;

const LeftSection = styled.div`
  flex: 2;
  min-width: 320px;
`;

const RightSection = styled.div`
  flex: 1;
  min-width: 280px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  padding: 25px;
  border-radius: 18px;
  height: fit-content;
  position: sticky;
  top: 50px;
  border: 1px solid rgba(255, 255, 255, 0.15);
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-top: 56.25%;
  margin-bottom: 25px;
  iframe,
  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 15px;
  }
`;

const CourseTitle = styled.h2`
  font-size: 2.4rem;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionAccordion = styled.div`
  background: #1e1e2f;
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const AccordionHeader = styled.div`
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  &:hover {
    background: #272743;
  }
`;

const AccordionContent = styled.div`
  padding: 0 20px 15px;
`;

const TopicItem = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 0;
  svg {
    color: #00d4ff;
    margin-right: 10px;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 15px;
  margin-bottom: 25px;
`;

const Price = styled.div`
  font-size: 2.2rem;
  font-weight: bold;
  color: #3cff0b;
`;

const OldPrice = styled.div`
  font-size: 1.2rem;
  text-decoration: line-through;
  color: #aaa;
`;

const Discount = styled.div`
  font-size: 0.9rem;
  background: #ff1744;
  color: #fff;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
`;

const CheckoutButton = styled(Link)`
  display: block;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  color: #fff;
  text-align: center;
  padding: 15px;
  border-radius: 50px;
  font-weight: bold;
  text-decoration: none;
  margin-bottom: 25px;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-3px);
    background: linear-gradient(135deg, #ff5722, #e91e63);
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 20px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  svg {
    color: #00d4ff;
    margin-right: 10px;
  }
`;

// ===== Main Component =====
const CourseDetailPage = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const docRef = doc(db, "courses", courseId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setCourse(snap.data());
        } else {
          console.error("Course not found ❌");
        }
      } catch (err) {
        console.error("Error fetching course:", err);
      }
    };
    fetchCourse();
  }, [courseId]);

  const toggleSection = (index) => {
    setOpenSections((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (!course) {
    return (
      <PageContainer>
        <h2 style={{ textAlign: "center" }}>Loading course...</h2>
      </PageContainer>
    );
  }

  // ✅ Calculate discounted price
  const discountedPrice =
    course.discount > 0
      ? (course.price - (course.price * course.discount) / 100).toFixed(2)
      : course.price;

  return (
    <PageContainer>
      <ContentWrapper>
        {/* Left Section */}
        <LeftSection>
          {course.demoVideoUrl ? (
            <VideoWrapper>
              <video src={course.demoVideoUrl} controls />
            </VideoWrapper>
          ) : course.imageUrl ? (
            <img
              src={course.imageUrl}
              alt={course.title}
              style={{
                width: "100%",
                borderRadius: "15px",
                marginBottom: "25px",
              }}
            />
          ) : null}

          <CourseTitle>{course.title}</CourseTitle>

          <h3>Description</h3>
          <p>{course.description}</p>

          <h3>Course Chapters</h3>
          {course.chapters && course.chapters.length > 0 ? (
            course.chapters.map((chapter, idx) => (
              <SectionAccordion key={idx}>
                <AccordionHeader onClick={() => toggleSection(idx)}>
                  {chapter.title}
                  {openSections[idx] ? <FaChevronUp /> : <FaChevronDown />}
                </AccordionHeader>
                {openSections[idx] && (
                  <AccordionContent>
                    <TopicItem>
                      <FaCheck /> Duration: {chapter.duration}
                    </TopicItem>
                    {chapter.videoUrl && (
                      <VideoWrapper>
                        <video src={chapter.videoUrl} controls />
                      </VideoWrapper>
                    )}
                  </AccordionContent>
                )}
              </SectionAccordion>
            ))
          ) : (
            <p>No chapters added yet.</p>
          )}
        </LeftSection>

        {/* Right Section */}
        <RightSection>
          {course.price === 0 ? (
            <Price style={{ color: "#00c853" }}>Free</Price>
          ) : course.discount > 0 ? (
            <PriceRow>
              <Price>₹{discountedPrice}</Price>
              <OldPrice>₹{course.price}</OldPrice>
              <Discount>{course.discount}% OFF</Discount>
            </PriceRow>
          ) : (
            <Price>₹{course.price}</Price>
          )}

          <CheckoutButton to={`/checkout/${courseId}`}>
            {course.price === 0 ? "Start Course" : "Buy Now"}
          </CheckoutButton>

          <h4>Course Info</h4>
          <FeatureList>
            <FeatureItem>
              <FaCheck /> Tutor: {course.tutor}
            </FeatureItem>
            <FeatureItem>
              <FaCheck /> Category: {course.category}
            </FeatureItem>
            <FeatureItem>
              <FaCheck /> Discount: {course.discount || 0}%
            </FeatureItem>
          </FeatureList>
        </RightSection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default CourseDetailPage;
